import { ContentAddress, DashPattern, DropShadow, LeaningShadow, Stroke } from 'editor/src/store/design/types';

import type { UploaderConfig } from 'shared/uploader';

export const enum ManipulationMode {
  perspectiveTransform = 'perspectiveTransform',
}

export interface EditorState {
  settings: Settings;
  defaultSettings: Settings;
  currentSpreadIndex: number;
  selectedElementUuids: number[];
  cropModeElementUuid: number | undefined;
  clipboardElementUuid: number | undefined;
  elementManipulationMode: { uuid: number; mode: ManipulationMode } | undefined;
  focusedMediaElementUuid: number | undefined;
  pluginPreviewMode:
    | {
        elementUuid: number;
        pluginName: string;
        assetId: string;
        url: string;
      }
    | undefined;
  unlockSizeRatioElementUuids: number[];
  disabledSpreads: DisabledSpread[];
  imagePatternScaleBases: Array<{
    value: number;
    dimension: 'width' | 'height';
    label: string;
  }>;
  trackDesignChanges: boolean;
  textEditMode: boolean;
  zoomMode: boolean;
  focusedContentAddress: ContentAddress | undefined;
  currentStyles: {
    line: { dashPattern: DashPattern };
    rectangle: { dashPattern: DashPattern };
    text: { shadow: DropShadow; stroke: Stroke; fontFamily: string; curve: number };
    image: { shadow: DropShadow | LeaningShadow };
  };
  uploaderConfig: UploaderConfig | undefined;
  dragState: 'local' | 'system' | undefined;
  isLoading: boolean;
  mobileMenu: {
    isOpen: boolean;
  };
  /**
   * True while a host-driven file picker session is in flight (the editor
   * emitted `uploader.openHostPicker` and is waiting for the host to either
   * send back assets or report a cancellation). Used to suppress the mobile
   * bottom-sheet's auto-close handlers (`<div .back onClick={close}>` and
   * `useDragging` global pointerup-without-movement-closes-menu) — on iOS
   * those fire from synthetic touches WKWebView receives when a Flutter
   * modal dismisses on top of it, prematurely collapsing the menu the user
   * is trying to come back to. Only meaningful when
   * `UploaderConfig.useHostFilePicker === true`; off by default.
   */
  hostPickerActive: boolean;
  /**
   * True once a save has been blocked by a regex forced-format mismatch
   * (see sendDesignDataPostMessage's gate). The Personalize tab's buyer-style
   * input otherwise only shows a mismatch after its own field has been
   * blurred, so an untouched field with a bad value would stay silent through
   * a blocked Continue — this flag is what makes it visible without waiting
   * for a blur that may never come.
   */
  showValueFormatErrors: boolean;
  personalizationStudioBannerData:
    | {
        productExamplesLink: string;
      }
    | undefined;
}

export interface DisabledSpread {
  spreadIndex: number;
  information: string;
}

export interface BaseDraggableItem {
  itemType: DraggableItemType;
  itemThumbnail?: string;
  sourceId?: string;
  dropCanvasCoords?: any;
  dropToElementUuid?: number;
  dimensions?: { width: number; height: number };
}

export interface DraggableSingleItem extends BaseDraggableItem {
  itemId: string;
  isMultipleItems: false;
}

export interface DraggableMultipleItems extends BaseDraggableItem {
  itemIds: string[];
  isMultipleItems: true;
}

export type DraggableItem = DraggableSingleItem | DraggableMultipleItems;

export enum DraggableItemType {
  none = 'none',
  image = 'image',
  assetImage = 'assetImage',
  addon = 'addon',
}
export type Unit = 'mm' | 'cm' | 'inch';

export enum SettingsProperty {
  showGrid = 'showGrid',
  showContentAreas = 'showContentAreas',
  showDescriptions = 'showDescriptions',
  showGuides = 'showGuides',
  showBleeds = 'showBleeds',
  showWrapsAndJoints = 'showWrapsAndJoints',
  units = 'units',
  showRulers = 'showRulers',
  showRollerStripes = 'showRollerStripes',
  showPerspectiveLines = 'showPerspectiveLines',
}
export interface Settings {
  [SettingsProperty.showGrid]: boolean;
  [SettingsProperty.showContentAreas]: boolean;
  [SettingsProperty.showDescriptions]: boolean;
  [SettingsProperty.showGuides]: boolean;
  [SettingsProperty.showBleeds]: boolean;
  [SettingsProperty.showWrapsAndJoints]: boolean;
  [SettingsProperty.units]: Unit;
  [SettingsProperty.showRulers]: boolean;
  [SettingsProperty.showRollerStripes]: boolean;
  [SettingsProperty.showPerspectiveLines]: boolean;
}
